import React, { useEffect, useState } from 'react';
import clsx from 'clsx';
import { makeStyles } from '@material-ui/styles';
import Skeleton from 'react-loading-skeleton';
import { SkeletonCardFields } from 'components/Loading';
import {
  Card,
  CardHeader,
  CardContent,
  Divider,
  Grid,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Checkbox,
  ListItemText,
  Input,
  Typography
} from '@material-ui/core';
import MomentUtils from '@date-io/moment';
import moment from 'moment';
import { TimePicker, MuiPickersUtilsProvider } from '@material-ui/pickers';
import {
  recurrenceTypes,
  daysOfWeek,
  monthlyOptionsTypes,
  daysOfWeekOrder
} from './constants';
import { Alert } from '@material-ui/lab';

import {
  getHumanReadableCronInLocalTime,
  getCronExpessionFromOptions,
  getOptionsFromCronExpression,
  convertCronExprToUTCMoment,
  getUTCTimeObjFromLocalMoment
} from './cronHelpers/';
import { SelectInput } from 'components';

const recurrenceTypesOptions = [
  { label: 'None', value: recurrenceTypes.NONE },
  { label: 'Daily', value: recurrenceTypes.DAILY },
  { label: 'Weekly', value: recurrenceTypes.WEEKLY },
  { label: 'Monthly', value: recurrenceTypes.MONTHLY }
];

const daysOfWeekOptions = [
  { label: 'Monday', value: daysOfWeek.MON },
  { label: 'Tuesday', value: daysOfWeek.TUE },
  { label: 'Wednesday', value: daysOfWeek.WED },
  { label: 'Thursday', value: daysOfWeek.THU },
  { label: 'Friday', value: daysOfWeek.FRI },
  { label: 'Saturday', value: daysOfWeek.SAT },
  { label: 'Sunday', value: daysOfWeek.SUN }
];

const monthlyOptions = [
  {
    label: 'On Day of Month',
    value: monthlyOptionsTypes.ON_DAY
  },
  {
    label: 'Last Day of Month',
    value: monthlyOptionsTypes.LAST_DAY
  },
  {
    label: 'Days Before End Of Month',
    value: monthlyOptionsTypes.DAYS_BEFORE_END
  }
];

const useStyles = makeStyles(theme => ({
  root: {
    flexGrow: 1
  },
  selectContainer: {
    display: 'inline',
    minWidth: '14rem',
    margin: theme.spacing(1)
  },
  inputContainer: {
    display: 'inline',
    minWidth: '14rem',
    margin: theme.spacing(1)
  },
  textFieldContainer: {
    margin: theme.spacing(1)
  },
  timeInputContainer: {
    marginLeft: theme.spacing(1),
    marginRight: theme.spacing(1)
  },
  daySelectContainer: {
    marginTop: theme.spacing(0.5),
    marginLeft: theme.spacing(1),
    marginRight: theme.spacing(1)
  },
  formControl: {
    width: '100%'
  },
  humanReadableCron: {
    marginLeft: theme.spacing(1)
  },
  alertInfo: {
    marginBottom: '1rem'
  }
}));

const getOptionPreSets = recurrenceType => {
  let result = {
    time: getUTCTimeObjFromLocalMoment(moment()),
    modifier: null,
    value: null
  };

  if (recurrenceType === recurrenceTypes.WEEKLY) {
    result.value = [
      moment()
        .format('ddd')
        .toUpperCase()
    ];
  }

  if (recurrenceType === recurrenceTypes.MONTHLY) {
    result.modifier = monthlyOptionsTypes.ON_DAY;
    result.value = 1;
  }

  return result;
};

const sortDaysOfWeek = (dayArr = []) =>
  dayArr.sort((a, b) => daysOfWeekOrder[a] - daysOfWeekOrder[b]);

const RecurrenceEditor = props => {
  const { className, campaign, ...rest } = props;
  const {
    thisCampaign,
    handleScheduleRuleChange,
    onBlur,
    onFocus,
    shouldShowError,
    isLoading,
    isCampaignReadOnly
  } = campaign;

  const classes = useStyles();
  const [recurrenceOptions, setRecurrenceOptions] = useState(
    getOptionsFromCronExpression(
      thisCampaign?.scheduleRule?.recurrenceExpression
    )
  );
  const [cronExpression, setCronExpression] = useState();
  const [humanReadableCron, setHumanReadableCron] = useState();

  useEffect(() => {
    if (
      thisCampaign?.scheduleRule?.recurrenceExpression &&
      !(
        recurrenceOptions.recurrenceType === recurrenceTypes.MONTHLY &&
        !recurrenceOptions.value
      )
    ) {
      let exp = getOptionsFromCronExpression(
        thisCampaign?.scheduleRule?.recurrenceExpression
      );
      setRecurrenceOptions(exp);
    }
  }, [thisCampaign]);

  useEffect(() => {
    let cronExp = getCronExpessionFromOptions(recurrenceOptions);
    setCronExpression(cronExp);
    setHumanReadableCron(getHumanReadableCronInLocalTime(cronExp));
  }, [recurrenceOptions]);

  const handleChange = event => {
    let updatedRecurrenceOptions = { ...recurrenceOptions };

    if (event.target.name === 'recurrenceType') {
      updatedRecurrenceOptions = getOptionPreSets(event.target.value || '');
    }

    if (
      updatedRecurrenceOptions.recurrenceType === recurrenceTypes.WEEKLY &&
      event.target.name === 'value'
    ) {
      event.target.value = sortDaysOfWeek(event.target.value);
    }

    updatedRecurrenceOptions[event.target.name] = event.target.value;
    setRecurrenceOptions(updatedRecurrenceOptions);
    handleScheduleRuleChange({
      recurrenceExpression: getCronExpessionFromOptions(
        updatedRecurrenceOptions
      )
    });
  };

  const handleTimeChange = mom => {
    let updatedRecurrenceOptions = { ...recurrenceOptions };

    let time = mom && getUTCTimeObjFromLocalMoment(mom);

    updatedRecurrenceOptions.time = time;

    setRecurrenceOptions(updatedRecurrenceOptions);
    handleScheduleRuleChange({
      recurrenceExpression: getCronExpessionFromOptions(
        updatedRecurrenceOptions
      )
    });
  };

  const displayTime = convertCronExprToUTCMoment(cronExpression).local();
  const getErrorMessage = () => {
    if (
      shouldShowError('scheduleRule') ||
      shouldShowError('scheduleRule.recurrenceExpression')
    )
      return 'Recurrence Frequency is required';
  };
  if (!thisCampaign.recurrence) {
    return null;
  }

  const SkeletonCardContent = props => {
    return (
      <CardContent>
        <Grid container spacing={0}>
          <Grid item xs={4}>
            <Skeleton />
          </Grid>
          <SkeletonCardFields numberOfRows={5} />
        </Grid>
      </CardContent>
    );
  };

  const getOptions = options => {
    return {
      list: options,
      keyId: 'value',
      valueId: 'value',
      displayValueId: 'label'
    };
  };

  return (
    <div className={classes.root}>
      <Card {...rest} className={clsx(classes.root, className)}>
        <form autoComplete="off">
          <CardHeader
            subheader="* indicates a required field"
            title="Recurrence"
            titleTypographyProps={{ variant: 'h1' }}
          />
          <Divider />
          {isLoading && <SkeletonCardContent />}
          {!isLoading && (
            <CardContent>
              <Alert severity="info" className={classes.alertInfo}>
                The selected date and time is based on your local time
              </Alert>
              <Grid container spacing={2} className={classes.root}>
                <Grid item xs={2} className={classes.selectContainer}>
                  <SelectInput
                    fullWidth
                    id="recurrenceFrequency"
                    label="Recurrence Frequency"
                    margin="dense"
                    name="recurrenceType"
                    disabled={isCampaignReadOnly()}
                    onChange={handleChange}
                    onFocus={onFocus}
                    onBlur={onBlur}
                    required
                    value={recurrenceOptions.recurrenceType || ''}
                    error={
                      shouldShowError('scheduleRule') ||
                      shouldShowError('scheduleRule.recurrenceExpression')
                    }
                    helperText={getErrorMessage()}
                    options={getOptions(recurrenceTypesOptions)}
                  />
                </Grid>

                {recurrenceOptions.recurrenceType ===
                  recurrenceTypes.WEEKLY && (
                  <Grid item xs={2} className={clsx(classes.inputContainer)}>
                    <FormControl
                      className={classes.formControl}
                      error={recurrenceOptions['value'].length === 0}
                      disabled={isCampaignReadOnly()}>
                      <InputLabel>Days Of Week</InputLabel>
                      <Select
                        multiple
                        name="value"
                        margin="dense"
                        required
                        value={recurrenceOptions.value || []}
                        onChange={handleChange}
                        input={<Input />}
                        renderValue={selected =>
                          selected.length === 7
                            ? 'Every Day'
                            : selected.join(', ')
                        }
                        MenuProps={{}}
                        variant="outlined">
                        {daysOfWeekOptions.map(day => (
                          <MenuItem key={day.value} value={day.value}>
                            <Checkbox
                              checked={
                                (recurrenceOptions.value || []).indexOf(
                                  day.value
                                ) > -1
                              }
                            />
                            <ListItemText primary={day.label} />
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                )}

                {recurrenceOptions.recurrenceType ===
                  recurrenceTypes.MONTHLY && (
                  <Grid item xs={3} className={clsx(classes.selectContainer)}>
                    <SelectInput
                      fullWidth
                      id="monthly-selector"
                      label="Monthly"
                      margin="dense"
                      name="modifier"
                      disabled={isCampaignReadOnly()}
                      onChange={handleChange}
                      onFocus={onFocus}
                      onBlur={onBlur}
                      required
                      value={recurrenceOptions.modifier || ''}
                      options={getOptions(monthlyOptions)}
                    />
                  </Grid>
                )}

                {(recurrenceOptions.modifier === monthlyOptionsTypes.ON_DAY ||
                  recurrenceOptions.modifier ===
                    monthlyOptionsTypes.DAYS_BEFORE_END) && (
                  <Grid
                    item
                    xs={2}
                    lg={2}
                    className={classes.textFieldContainer}>
                    <TextField
                      fullWidth
                      label="Days"
                      id="monthly-days-input"
                      margin="dense"
                      name="value"
                      required
                      disabled={isCampaignReadOnly()}
                      onChange={handleChange}
                      onFocus={onFocus}
                      onBlur={onBlur}
                      value={recurrenceOptions.value}
                      variant="outlined"
                      helperText={
                        !recurrenceOptions.value &&
                        'On Empty will use default value'
                      }></TextField>
                  </Grid>
                )}

                {recurrenceOptions.recurrenceType !== recurrenceTypes.NONE && (
                  <MuiPickersUtilsProvider utils={MomentUtils}>
                    <Grid
                      item
                      md={3}
                      xs={3}
                      className={classes.timeInputContainer}>
                      <TimePicker
                        views={['hours', 'minutes', 'seconds']}
                        margin="dense"
                        id="time-picker"
                        format="hh:mm:ss a"
                        name="scheduleRuleValue"
                        label="Time"
                        required
                        value={displayTime}
                        disabled={isCampaignReadOnly()}
                        onChange={handleTimeChange}
                      />
                    </Grid>
                  </MuiPickersUtilsProvider>
                )}
              </Grid>
              <Grid container spacing={2} className={classes.humanReadableCron}>
                {humanReadableCron && (
                  <Typography id="summary" variant="body1" display="block">
                    Recurring: {humanReadableCron}
                  </Typography>
                )}
              </Grid>
            </CardContent>
          )}
        </form>
      </Card>
    </div>
  );
};

export default RecurrenceEditor;
